import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import type { ResourceCardData } from '@/features/resources/types';

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

interface ResourceCardProps {
  data: ResourceCardData;
  onUnlink: (id: string) => void;
  onNavigate: (data: ResourceCardData) => void;
  borderColor?: string;
}

export function ResourceCard({ data, onUnlink, onNavigate, borderColor = 'border-transparent' }: ResourceCardProps) {
  const { t } = useI18n();

  return (
    <div
      className={`group flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent/50 cursor-pointer border-l-2 ${borderColor}`}
      onClick={() => onNavigate(data)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onNavigate(data); }}
    >
      {data.icon ? (
        <svg className="h-4 w-4 flex-shrink-0" style={{ color: data.color || undefined }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d={data.icon} />
        </svg>
      ) : (
        <div className="h-4 w-4 flex-shrink-0 rounded-sm bg-muted" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-rail-foreground truncate">{data.title}</p>
        {data.subtitle && (
          <p className="text-xs text-muted-foreground truncate">{data.subtitle}</p>
        )}
      </div>
      {data.status && (
        <Badge variant={data.statusVariant as any} size="sm" className="group-hover:hidden flex-shrink-0">
          {data.status}
        </Badge>
      )}
      <div className="hidden group-hover:flex items-center gap-1 flex-shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); onUnlink(data.id); }}
          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
          aria-label={t('rail.resources.unlink')}
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
